import { useReducer, useRef, useCallback, useEffect } from "react";
import { initiateAadhaarKyc, fetchAadhaarStatus, getDigioVerificationStatus } from "../services/kycService";
import { getProfile } from "../services/authService";

const initialState = {
  status: "idle",       // idle | initiating | iframe | polling | verified | failed
  documentId: null,
  signInUrl: null,
  aadhaarData: null,
  error: null,
  digioVerified: false,
};

function reducer(state, action) {
  switch (action.type) {
    case "INITIATING":     return { ...state, status: "initiating", error: null };
    case "SHOW_IFRAME":    return { ...state, status: "iframe", documentId: action.documentId, signInUrl: action.signInUrl };
    case "POLLING":        return { ...state, status: "polling" };
    case "VERIFIED":       return { ...state, status: "verified", aadhaarData: action.data, digioVerified: action.digioVerified ?? state.digioVerified };
    case "DIGIO_VERIFIED": return { ...state, digioVerified: true };
    case "FAILED":         return { ...state, status: "failed", error: action.error };
    case "RESET":          return { ...initialState };
    default:               return state;
  }
}

export function useAadhaarKyc({ dob, onVerified } = {}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const pollRef  = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const checkDigio = useCallback(async (autoFillCb) => {
    try {
      const res = await getDigioVerificationStatus();
      const d = res.data?.data;
      if (!d?.aadhaar?.verified) return false;
      dispatch({ type: "DIGIO_VERIFIED" });
      const ad = d.aadhaar?.data;
      if (ad && autoFillCb) {
        let parsedDob = ad.dob || "";
        if (parsedDob.includes("/")) {
          const [dd, mm, yyyy] = parsedDob.split("/");
          parsedDob = `${yyyy}-${mm}-${dd}`;
        }
        autoFillCb({ name: ad.name, dob: parsedDob, aadhaar: ad.aadhaar_number });
      }
      return true;
    } catch { return false; }
  }, []);

  const startPolling = useCallback((documentId, autoFillCb) => {
    dispatch({ type: "POLLING" });
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetchAadhaarStatus(documentId);
        const d = res.data?.data;
        const s = d?.status || d?.kyc_status || "";

        if (s === "success" || s === "completed") {
          stopPolling();
          const digioOk = await checkDigio(autoFillCb);
          dispatch({ type: "VERIFIED", data: d, digioVerified: digioOk });
          onVerified && onVerified(d);
          return;
        }
        if (s === "failed" || s === "rejected") {
          stopPolling();
          dispatch({ type: "FAILED", error: d?.message || "Aadhaar verification failed." });
          return;
        }
        await checkDigio(autoFillCb);
      } catch (err) {
        console.warn("Aadhaar poll error:", err?.message);
      }
    }, 7000);
  }, [stopPolling, checkDigio, onVerified]);

  // Listen for postMessage from iframe — Digio sends events when OTP done
  const setupIframeListener = useCallback((documentId, autoFillCb, signInUrl) => {
    const handler = (event) => {
      try {
        const msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        console.log("iframe message:", msg);

        // Digio sends digilocker redirect URL or completion event
        const isDigilockerStep =
          msg?.action === "digilocker" ||
          msg?.type   === "digilocker" ||
          msg?.step   === "digilocker" ||
          msg?.status === "otp_verified" ||
          msg?.redirect_url?.includes("digilocker") ||
          msg?.url?.includes("digilocker");

        if (isDigilockerStep) {
          const digiUrl = msg?.redirect_url || msg?.url || signInUrl;
          window.removeEventListener("message", handler);
          window.open(digiUrl, "_blank", "noopener,noreferrer");
          startPolling(documentId, autoFillCb);
          return;
        }

        // Generic completion
        const isDone =
          msg?.status === "success" ||
          msg?.status === "completed" ||
          msg?.action === "completed";

        if (isDone) {
          window.removeEventListener("message", handler);
          startPolling(documentId, autoFillCb);
        }
      } catch { /* non-JSON messages ignore */ }
    };

    window.addEventListener("message", handler);
    // Also start polling after 30s as fallback even if no postMessage
    setTimeout(() => {
      window.removeEventListener("message", handler);
      if (stateRef.current.status === "iframe") {
        startPolling(documentId, autoFillCb);
      }
    }, 30000);

    return () => window.removeEventListener("message", handler);
  }, [startPolling]);

  const initiate = useCallback(async (autoFillCb) => {
    if (stateRef.current.status === "initiating") return;
    dispatch({ type: "INITIATING" });
    try {
      const profileRes = await getProfile();
      const userData = profileRes.data?.data || profileRes.data || {};
      const customerIdentifier = userData?.mobile || "";

      if (!customerIdentifier) {
        dispatch({ type: "FAILED", error: "Mobile number not found in profile." });
        return;
      }

      let formattedDob = "";
      if (dob && dob.includes("-")) {
        const [y, m, d] = dob.split("-");
        formattedDob = `${d}/${m}/${y}`;
      }

      const res        = await initiateAadhaarKyc(customerIdentifier, formattedDob);
      const data       = res.data?.data;
      const documentId = data?.document_id || data?.id;
      const signInUrl  = data?.signing_url  || data?.sign_in_url || data?.url;

      if (!documentId || !signInUrl) {
        dispatch({ type: "FAILED", error: "Invalid response from server. Please try again." });
        return;
      }

      // Open directly in new window
      window.open(signInUrl, "_blank", "noopener,noreferrer");
      dispatch({ type: "SHOW_IFRAME", documentId, signInUrl });
      startPolling(documentId, autoFillCb);
    } catch (err) {
      dispatch({ type: "FAILED", error: err.response?.data?.message || "Failed to initiate Aadhaar KYC." });
    }
  }, [dob, setupIframeListener]);

  // Called from widget when user manually clicks "Open Digilocker"
  const openDigilocker = useCallback(() => {
    if (stateRef.current.signInUrl) {
      window.open(stateRef.current.signInUrl, "_blank", "noopener,noreferrer");
    }
  }, []);

  const reset = useCallback(() => { stopPolling(); dispatch({ type: "RESET" }); }, [stopPolling]);

  return { ...state, initiate, openDigilocker, reset };
}
